<template>
  <div
    class="nuxt-rating-wrapper"
    :class="{ 'nuxt-rating-inline': props.inline }"
    @mouseleave="resetRating"
  >
    <span
      v-for="n in props.ratingCount"
      :key="n"
      :class="[
        { 'nuxt-rating-pointer': !props.readOnly, 'skeleton-pulse': isLoading },
        'nuxt-rating-star',
      ]"
      :style="{
        marginRight: n !== props.ratingCount ? `${props.ratingSpacing}px` : '0',
      }"
    >
      <NuxtStar
        :fill="isLoading ? 100 : (fillLevel[n - 1] ?? 0)"
        :size="props.ratingSize"
        :points="props.ratingContent"
        :star-id="n"
        :step="step"
        :active-color="isLoading ? props.skeletonColor : currentActiveColor"
        :inactive-color="props.inactiveColor"
        :border-color="isLoading ? props.skeletonColor : props.borderColor"
        :border-width="props.borderWidth"
        :rounded-corners="props.roundedCorners"
        @star-selected="setRating($event, true)"
        @star-mouse-move="setRating"
      />
    </span>
  </div>
</template>

<script lang="ts" setup>
// nuxt-rating v0.1.6 (MIT License, https://github.com/TonyFresneau/nuxt-rating) の
// dist/runtime/components/NuxtRating.vue を vendor 取り込みしたもの。
// upstream が noUncheckedIndexedAccess 非対応かつメンテナンスが停滞しているため自前管理する。
// 取り込みにあたり、型定義(NuxtRatingProps)をこのファイルへ内包し、
// noUncheckedIndexedAccess でエラーになる箇所(fillLevel / activeColors の index アクセス)を修正している。
// また、activeColor 配列が ratingCount より長い場合に Array(負数) で RangeError になる
// upstream の潜在バグを padColors 内で修正している。
//
// MIT の許諾表示を配信物にも残すため /*! 形式で記述する(通常の // コメントは除去される)。
// SSR バンドルには残るが、クライアント側は Vite 8 の transform が terser に渡る前に
// 除去するため残らない。バンドル全体の第三者ライセンス表記は別課題として扱う。
/*!
 * nuxt-rating v0.1.6 - Copyright (c) 2023 Aurion SARL - MIT License
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions: The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { computed, ref, watch, onMounted } from 'vue'
import NuxtStar from './NuxtStar.vue'

interface NuxtRatingProps {
  ratingStep?: number
  ratingValue?: number
  roundStartRating?: boolean
  activeColor?: string | string[]
  inactiveColor?: string
  skeletonColor?: string
  ratingCount?: number
  ratingContent?: number[]
  ratingSize?: number | string
  ratingSpacing?: number
  readOnly?: boolean
  inline?: boolean
  borderColor?: string
  borderWidth?: number
  roundedCorners?: boolean
  clearable?: boolean
}

interface RatingEvent {
  id: number
  position: number
}

const props = withDefaults(defineProps<NuxtRatingProps>(), {
  ratingStep: 0.1,
  ratingValue: 4.5,
  roundStartRating: true,
  activeColor: '#ffa41c',
  inactiveColor: '#d8d8d8',
  skeletonColor: '#d8d8d8',
  ratingCount: 5,
  ratingContent: () => [
    19.8, 2.2, 6.6, 43.56, 39.6, 17.16, 0, 17.16, 33, 43.56,
  ],
  ratingSize: 15,
  ratingSpacing: 2,
  readOnly: true,
  inline: false,
  borderColor: '#db8403',
  borderWidth: 0,
  roundedCorners: false,
  clearable: false,
})

const emit = defineEmits(['ratingSelected', 'ratingHovered'])

const step = ref(props.ratingStep * 100)
const currentRating = ref(props.ratingValue)
const selectedRating = ref(props.ratingValue)
const ratingSelected = ref(false)
const isLoading = ref(true)

const shouldRound = computed(
  () => ratingSelected.value || props.roundStartRating
)

const activeColors = computed(() =>
  Array.isArray(props.activeColor)
    ? padColors(
        props.activeColor,
        props.ratingCount,
        props.activeColor.slice(-1)[0] ?? props.inactiveColor
      )
    : new Array(props.ratingCount).fill(props.activeColor)
)

const currentActiveColor = computed(() =>
  selectedRating.value > 0
    ? (activeColors.value[Math.ceil(selectedRating.value) - 1] ??
      props.inactiveColor)
    : props.inactiveColor
)

const roundedRating = computed(() => {
  const inv = 1.0 / props.ratingStep
  return Math.min(props.ratingCount, Math.ceil(currentRating.value * inv) / inv)
})

const fillLevel = computed(() => {
  const rating = shouldRound.value ? roundedRating.value : currentRating.value
  return Array.from({ length: props.ratingCount }, (_, i) => {
    if (i < rating) {
      return rating - i > 1 ? 100 : (rating - i) * 100
    }
    return 0
  })
})

watch(
  () => props.ratingValue,
  (val) => {
    currentRating.value = val
    selectedRating.value = val
  }
)

onMounted(() => {
  isLoading.value = false
})

const setRating = (event: RatingEvent, persist: boolean) => {
  if (!props.readOnly) {
    const position = Number(event.position) / 100
    const newRating =
      event.id -
      1 +
      Math.ceil(position * (1 / props.ratingStep)) * props.ratingStep
    currentRating.value = Math.min(newRating, props.ratingCount)

    const decimalPlaces = props.ratingStep.toString().split('.')[1]?.length || 0
    const formattedRating = Number(currentRating.value.toFixed(decimalPlaces))

    if (persist) {
      selectedRating.value =
        props.clearable && currentRating.value === selectedRating.value
          ? 0
          : currentRating.value
      ratingSelected.value = true
      emit('ratingSelected', formattedRating)
    }
    emit('ratingHovered', formattedRating)
  }
}

const resetRating = () => {
  if (!props.readOnly) {
    currentRating.value = selectedRating.value
    const decimalPlaces = props.ratingStep.toString().split('.')[1]?.length || 0
    const formattedRating = Number(currentRating.value.toFixed(decimalPlaces))
    emit('ratingHovered', formattedRating)
  }
}

const padColors = (array: string[], minLength: number, fillValue: string) =>
  // 配列が minLength より長い場合に Array(負数) が RangeError を投げないよう 0 でクランプする
  array.concat(Array(Math.max(0, minLength - array.length)).fill(fillValue))
</script>

<style scoped>
.nuxt-rating-star {
  display: inline-block;
  -webkit-tap-highlight-color: transparent;
}
.nuxt-rating-pointer {
  cursor: pointer;
}
.nuxt-rating {
  align-items: center;
  display: flex;
}
.nuxt-rating-inline {
  display: inline-flex;
}
.skeleton-wrapper {
  align-items: center;
  display: flex;
}
.skeleton-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
@keyframes pulse {
  0% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
  to {
    opacity: 0.4;
  }
}
</style>
